import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  logActivity,
  logMaintenanceActivity,
  logCustomerActivity,
  logUserActivity
} from '../services/activityLogService';
import {
  ActivityType,
  ActivityLevel,
  ActivityStatus
} from '../types/activityLog';

/**
 * Hook لتسجيل النشاط بسهولة
 */
export const useActivityLogger = () => {
  const { currentUser } = useAuth();

  // الحصول على معلومات IP والمتصفح
  const getClientInfo = useCallback(() => {
    return {
      userAgent: navigator.userAgent,
      // يمكن إضافة خدمة للحصول على IP الحقيقي
      ipAddress: 'Unknown'
    };
  }, []);

  // تسجيل نشاط عام
  const log = useCallback(async (
    type: ActivityType,
    title: string,
    description: string,
    targetType: string,
    targetId: string,
    options: {
      level?: ActivityLevel;
      status?: ActivityStatus;
      targetName?: string;
      oldData?: any;
      newData?: any;
      changes?: string[];
      metadata?: any;
    } = {}
  ) => {
    if (!currentUser) {
      console.warn('Cannot log activity: No current user');
      return;
    }

    try {
      const clientInfo = getClientInfo();
      
      await logActivity(
        type,
        title,
        description,
        currentUser.id,
        currentUser.name || currentUser.email || 'مستخدم غير معروف',
        currentUser.role || 'user',
        targetType,
        targetId,
        {
          ...options,
          userBranch: currentUser.branchId,
          ...clientInfo
        }
      );
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  }, [currentUser, getClientInfo]);

  // تسجيل أنشطة الصيانة
  const logMaintenance = useCallback(async (
    type: ActivityType,
    maintenanceId: string,
    maintenanceName: string,
    description: string,
    options: {
      level?: ActivityLevel;
      status?: ActivityStatus;
      oldData?: any;
      newData?: any;
      changes?: string[];
    } = {}
  ) => {
    if (!currentUser) return;

    try {
      await logMaintenanceActivity(
        type,
        maintenanceId,
        maintenanceName,
        currentUser.id,
        currentUser.name || currentUser.email || 'مستخدم غير معروف',
        currentUser.role || 'user',
        description,
        options
      );
    } catch (error) {
      console.error('Error logging maintenance activity:', error);
    }
  }, [currentUser]);

  // تسجيل أنشطة العملاء
  const logCustomer = useCallback(async (
    type: ActivityType,
    customerId: string,
    customerName: string,
    description: string,
    options: {
      level?: ActivityLevel;
      status?: ActivityStatus;
      oldData?: any;
      newData?: any;
      changes?: string[];
    } = {}
  ) => {
    if (!currentUser) return;

    try {
      await logCustomerActivity(
        type,
        customerId,
        customerName,
        currentUser.id,
        currentUser.name || currentUser.email || 'مستخدم غير معروف',
        currentUser.role || 'user',
        description,
        options
      );
    } catch (error) {
      console.error('Error logging customer activity:', error);
    }
  }, [currentUser]);

  // تسجيل أنشطة المستخدمين
  const logUser = useCallback(async (
    type: ActivityType,
    targetUserId: string,
    targetUserName: string,
    description: string,
    options: {
      level?: ActivityLevel;
      status?: ActivityStatus;
      oldData?: any;
      newData?: any;
      changes?: string[];
    } = {}
  ) => {
    if (!currentUser) return;

    try {
      await logUserActivity(
        type,
        targetUserId,
        targetUserName,
        currentUser.id,
        currentUser.name || currentUser.email || 'مستخدم غير معروف',
        currentUser.role || 'user',
        description,
        options
      );
    } catch (error) {
      console.error('Error logging user activity:', error);
    }
  }, [currentUser]);

  // دوال مخصصة لأنشطة شائعة

  // تسجيل إنشاء طلب صيانة
  const logMaintenanceCreate = useCallback(async (
    maintenanceId: string,
    customerName: string,
    deviceType: string,
    data: any
  ) => {
    await logMaintenance(
      ActivityType.MAINTENANCE_CREATE,
      maintenanceId,
      `${customerName} - ${deviceType}`,
      `تم إنشاء طلب صيانة جديد للعميل ${customerName}`,
      {
        level: ActivityLevel.MEDIUM,
        newData: data
      }
    );
  }, [logMaintenance]);

  // تسجيل تحديث طلب صيانة
  const logMaintenanceUpdate = useCallback(async (
    maintenanceId: string,
    customerName: string,
    deviceType: string,
    oldData: any,
    newData: any,
    changes: string[]
  ) => {
    await logMaintenance(
      ActivityType.MAINTENANCE_UPDATE,
      maintenanceId,
      `${customerName} - ${deviceType}`,
      `تم تحديث طلب الصيانة: ${changes.join(', ')}`,
      {
        level: ActivityLevel.MEDIUM,
        oldData,
        newData,
        changes
      }
    );
  }, [logMaintenance]);

  // تسجيل حذف طلب صيانة
  const logMaintenanceDelete = useCallback(async (
    maintenanceId: string,
    customerName: string,
    deviceType: string,
    data: any
  ) => {
    await logMaintenance(
      ActivityType.MAINTENANCE_DELETE,
      maintenanceId,
      `${customerName} - ${deviceType}`,
      `تم حذف طلب الصيانة للعميل ${customerName}`,
      {
        level: ActivityLevel.HIGH,
        oldData: data
      }
    );
  }, [logMaintenance]);

  // تسجيل تغيير حالة الصيانة
  const logMaintenanceStatusChange = useCallback(async (
    maintenanceId: string,
    customerName: string,
    deviceType: string,
    oldStatus: string,
    newStatus: string
  ) => {
    await logMaintenance(
      ActivityType.MAINTENANCE_STATUS_CHANGE,
      maintenanceId,
      `${customerName} - ${deviceType}`,
      `تم تغيير حالة الصيانة من "${oldStatus}" إلى "${newStatus}"`,
      {
        level: ActivityLevel.MEDIUM,
        oldData: { status: oldStatus },
        newData: { status: newStatus },
        changes: ['status']
      }
    );
  }, [logMaintenance]);

  // تسجيل حذف عميل
  const logCustomerDelete = useCallback(async (
    customerId: string,
    customerName: string,
    data: any
  ) => {
    await logCustomer(
      ActivityType.CUSTOMER_DELETE,
      customerId,
      customerName,
      `تم حذف العميل ${customerName}`,
      {
        level: ActivityLevel.HIGH,
        oldData: data
      }
    );
  }, [logCustomer]);

  // تسجيل رفع صورة
  const logImageUpload = useCallback(async (
    targetType: string,
    targetId: string,
    targetName: string,
    imageUrl: string,
    imageType: string
  ) => {
    await log(
      ActivityType.IMAGE_UPLOAD,
      `رفع صورة ${imageType}`,
      `تم رفع صورة ${imageType} للكائن ${targetName}`,
      targetType,
      targetId,
      {
        level: ActivityLevel.LOW,
        targetName,
        metadata: {
          imageUrl,
          imageType
        }
      }
    );
  }, [log]);

  // تسجيل حذف صورة
  const logImageDelete = useCallback(async (
    targetType: string,
    targetId: string,
    targetName: string,
    imageUrl: string,
    imageType: string
  ) => {
    await log(
      ActivityType.IMAGE_DELETE,
      `حذف صورة ${imageType}`,
      `تم حذف صورة ${imageType} للكائن ${targetName}`,
      targetType,
      targetId,
      {
        level: ActivityLevel.MEDIUM,
        targetName,
        metadata: {
          imageUrl,
          imageType
        }
      }
    );
  }, [log]);

  // تسجيل تسجيل الدخول
  const logLogin = useCallback(async () => {
    if (!currentUser) return;

    await log(
      ActivityType.USER_LOGIN,
      'تسجيل دخول',
      `قام المستخدم بتسجيل الدخول`,
      'user',
      currentUser.id,
      {
        level: ActivityLevel.LOW,
        targetName: currentUser.name || currentUser.email
      }
    );
  }, [log, currentUser]);

  // تسجيل تسجيل الخروج
  const logLogout = useCallback(async () => {
    if (!currentUser) return;

    await log(
      ActivityType.USER_LOGOUT,
      'تسجيل خروج',
      `قام المستخدم بتسجيل الخروج`,
      'user',
      currentUser.id,
      {
        level: ActivityLevel.LOW,
        targetName: currentUser.name || currentUser.email
      }
    );
  }, [log, currentUser]);

  // تسجيل دفعة صيانة
  const logMaintenancePayment = useCallback(async (
    maintenanceId: string,
    amount: number,
    paymentType: string,
    customerName: string,
    employeeName: string
  ) => {
    await log(
      ActivityType.MAINTENANCE_UPDATE,
      `دفعة صيانة ${paymentType === 'cash' ? 'نقدية' : 'دين'}`,
      `تم تسديد ${amount} جنيه ${paymentType === 'cash' ? 'نقداً' : 'ديناً'} لصيانة العميل ${customerName} بواسطة ${employeeName}`,
      'maintenance',
      maintenanceId,
      {
        level: ActivityLevel.MEDIUM,
        targetName: `${customerName} - ${maintenanceId.substring(0, 8)}`,
        metadata: {
          amount,
          paymentType,
          customerName,
          employeeName
        }
      }
    );
  }, [log]);

  return {
    // دوال عامة
    log,
    logMaintenance,
    logCustomer,
    logUser,

    // دوال مخصصة
    logMaintenanceCreate,
    logMaintenanceUpdate,
    logMaintenanceDelete,
    logMaintenanceStatusChange,
    logMaintenancePayment,
    logCustomerDelete,
    logImageUpload,
    logImageDelete,
    logLogin,
    logLogout
  };
};

export default useActivityLogger;
